import { FavoritesService } from "@services/favorites.service";
import { StatePersistenceService } from "@services/state-persistence.service";
import {
  hasValidOptionalProperty,
  isArrayOf,
  isNullableString,
  isRecord,
  isString,
} from "@utils/state-validation.utils";
import { catchError, concatMap, EMPTY, map, of, Subject, tap } from "rxjs";

import { HttpErrorResponse } from "@angular/common/http";
import {
  computed,
  DestroyRef,
  effect,
  inject,
  Service,
  signal,
} from "@angular/core";
import { rxResource, takeUntilDestroyed } from "@angular/core/rxjs-interop";

interface FavoritesStateModel {
  tenant: string | null;
  favoriteKeys: string[];
}

interface FavoriteSave {
  tenant: string;
  favoriteKeys: string[];
  session: number;
}

const DEFAULT_STATE: FavoritesStateModel = {
  tenant: null,
  favoriteKeys: [],
};

function isPersistedFavoritesState(
  value: unknown
): value is Partial<FavoritesStateModel> {
  return (
    isRecord(value) &&
    hasValidOptionalProperty(value, "tenant", isNullableString) &&
    hasValidOptionalProperty(value, "favoriteKeys", keys =>
      isArrayOf(keys, isString)
    )
  );
}

@Service()
export class FavoritesStore {
  private readonly favoritesService = inject(FavoritesService);
  private readonly persistence = inject(StatePersistenceService);
  private readonly destroyRef = inject(DestroyRef);
  private tenant = signal<string | null>(null);
  private request = signal<string | undefined>(undefined);
  private pendingSave = signal<FavoriteSave | null>(null);
  private readonly saves = new Subject<FavoriteSave>();
  private session = 0;
  private resource = rxResource({
    params: () => this.request(),
    stream: ({ params: tenant }) =>
      this.favoritesService.getFavorites(tenant).pipe(
        map(response => response.data),
        catchError(error => {
          if (!(error instanceof HttpErrorResponse && error.status === 500)) {
            console.error("Unable to load favorites", error);
          }
          return of([]);
        })
      ),
    defaultValue: DEFAULT_STATE.favoriteKeys,
  });

  favoriteKeys = computed(
    () => this.pendingSave()?.favoriteKeys ?? this.resource.value()
  );

  constructor() {
    const persisted = this.hydrateState();
    this.tenant.set(persisted.tenant);
    this.resource.set(persisted.favoriteKeys);
    effect(() => {
      const tenant = this.tenant();
      if (this.resource.hasValue()) {
        this.persistence.write("Favorites", {
          tenant,
          favoriteKeys: this.resource.value(),
        });
      }
    });
    this.saves
      .pipe(
        concatMap(save => {
          if (save.session !== this.session) return EMPTY;
          return this.favoritesService
            .saveFavorites(save.tenant, save.favoriteKeys)
            .pipe(
              tap(response => {
                if (save.session !== this.session) return;
                this.resource.set(response.data);
                if (this.pendingSave() === save) this.pendingSave.set(null);
              }),
              catchError(error => {
                console.error("Unable to save favorites", error);
                if (this.pendingSave() === save) this.pendingSave.set(null);
                return EMPTY;
              })
            );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  load(tenant: string) {
    if (this.tenant() !== tenant) this.resetForTenant(tenant);

    if (this.request() === tenant) return this.resource.reload();

    this.request.set(tenant);
    return true;
  }

  save(favoriteKeys: string[]) {
    const tenant = this.tenant();
    if (!tenant) return;

    const save = {
      tenant,
      favoriteKeys: [...favoriteKeys],
      session: this.session,
    };
    this.pendingSave.set(save);
    this.saves.next(save);
  }

  clear() {
    this.resetForTenant(null);
  }

  private hydrateState() {
    const persisted = this.persistence.read<Partial<FavoritesStateModel>>(
      "Favorites",
      {},
      isPersistedFavoritesState
    );
    return {
      tenant: persisted.tenant ?? DEFAULT_STATE.tenant,
      favoriteKeys: persisted.favoriteKeys ?? DEFAULT_STATE.favoriteKeys,
    };
  }

  private resetForTenant(tenant: string | null) {
    this.session += 1;
    this.request.set(undefined);
    this.pendingSave.set(null);
    this.tenant.set(tenant);
    this.resource.set([]);
  }
}
